export const TEARSHEET_KPI_CURRENT_EXPOSURE = 'Current Exposure';
export const TEARSHEET_KPI_POST_FUNDING_EXPOSURE = 'Post-Funding Exposure';
export const TEARSHEET_KPI_NEXT_REPAYMENT = 'Next Repayment';
export const TEARSHEET_KPI_MAX_MODELED_EXPOSURE = 'Max Modeled Exposure';
export const TEARSHEET_KPI_LEGACY_FUTURE_EXPOSURE =
  'Future Exposure (post-funding)';

export const TEARSHEET_EXPOSURE_ORDER_BY_KPI = {
  [TEARSHEET_KPI_CURRENT_EXPOSURE]: 0,
  [TEARSHEET_KPI_POST_FUNDING_EXPOSURE]: 1,
  [TEARSHEET_KPI_LEGACY_FUTURE_EXPOSURE]: 1,
  [TEARSHEET_KPI_NEXT_REPAYMENT]: 2,
  [TEARSHEET_KPI_MAX_MODELED_EXPOSURE]: 3,
};

export function displayTearsheetCovenantKpi(kpi) {
  if (kpi === TEARSHEET_KPI_LEGACY_FUTURE_EXPOSURE) {
    return TEARSHEET_KPI_POST_FUNDING_EXPOSURE;
  }
  if (kpi === 'MoM Growth (Last Month)' || kpi === 'Max MOM % increase (Last Month)') {
    return 'Max MoM % increase (Last Month)';
  }
  if (
    kpi === 'MoM Growth (Current Month)' ||
    kpi === 'Max MOM % increase (Current Month)'
  ) {
    return 'Max MoM % increase (Current Month)';
  }
  if (kpi === 'Funding % (MoM increase)' || kpi === 'Max MOM % increase') {
    return 'Max MoM % increase';
  }
  return kpi;
}

export function isPostFundingExposureKpi(kpi) {
  return (
    kpi === TEARSHEET_KPI_POST_FUNDING_EXPOSURE ||
    kpi === TEARSHEET_KPI_LEGACY_FUTURE_EXPOSURE
  );
}

export function orderTearsheetExposureResultRows(resultRows) {
  const rows = resultRows ?? [];
  return rows
    .map((row, index) => ({ row, index }))
    .sort((leftRow, rightRow) => {
      if (leftRow.row?.area !== rightRow.row?.area) {
        return leftRow.index - rightRow.index;
      }
      if (leftRow.row?.area === 'Exposure') {
        const leftOrder =
          TEARSHEET_EXPOSURE_ORDER_BY_KPI[leftRow.row?.kpi] ??
          Number.MAX_SAFE_INTEGER;
        const rightOrder =
          TEARSHEET_EXPOSURE_ORDER_BY_KPI[rightRow.row?.kpi] ??
          Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
      }
      return leftRow.index - rightRow.index;
    })
    .map(({ row }) => row);
}

export function buildCurrentExposureTooltip() {
  return (
    'Sum of Principal Due Outstanding and Fee Due Outstanding across non-preview installments ' +
    '(asset tape). Preview installments are excluded from current exposure.'
  );
}

export function buildPostFundingExposureTooltip({
  fetchResult,
  formatCurrency,
}) {
  if (!fetchResult || typeof formatCurrency !== 'function') {
    return null;
  }
  const currentExposure = fetchResult.currentExposure;
  const requestedFunding = fetchResult.requestedFundingAmount;
  const nextRepayment = fetchResult.nextRepaymentAmount;
  const postFunding = fetchResult.futureExposure;

  if (
    currentExposure == null &&
    requestedFunding == null &&
    nextRepayment == null &&
    postFunding == null
  ) {
    return null;
  }

  const lines = [
    'Post-funding exposure is current exposure plus requested funding, minus the open current-month repayment, plus any preview installment contribution when applicable.',
  ];
  if (currentExposure != null && Number.isFinite(Number(currentExposure))) {
    lines.push(`Current exposure: ${formatCurrency(Number(currentExposure))}`);
  }
  if (
    requestedFunding != null &&
    Number.isFinite(Number(requestedFunding))
  ) {
    lines.push(
      `+ Requested funding: ${formatCurrency(Number(requestedFunding))}`
    );
  }
  if (nextRepayment != null && Number.isFinite(Number(nextRepayment))) {
    lines.push(
      `− Next repayment (current month): ${formatCurrency(Number(nextRepayment))}`
    );
  }
  const previewContribution =
    currentExposure != null &&
    requestedFunding != null &&
    nextRepayment != null &&
    postFunding != null &&
    Number.isFinite(Number(currentExposure)) &&
    Number.isFinite(Number(requestedFunding)) &&
    Number.isFinite(Number(nextRepayment)) &&
    Number.isFinite(Number(postFunding))
      ? Number(postFunding) -
        Number(currentExposure) -
        Number(requestedFunding) +
        Number(nextRepayment)
      : null;
  if (
    previewContribution != null &&
    Number.isFinite(previewContribution) &&
    Math.abs(previewContribution) > 0.005
  ) {
    lines.push(
      `+ Preview installment: ${formatCurrency(previewContribution)}`
    );
  }
  if (postFunding != null && Number.isFinite(Number(postFunding))) {
    lines.push(
      `= Post-funding exposure: ${formatCurrency(Number(postFunding))}`
    );
  }
  return lines.join('\n');
}

export function buildMaxModeledExposureTooltip() {
  return (
    'Peak Twelve Ending Balance from MOB 0 onward in the modeled cashflow, ' +
    'including projected draws and repayments through the forecast. ' +
    'Compared against the max exposure covenant when set.'
  );
}
